/**
 * Page d'inscription pour les clients publics via identifiants ANEF
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Alert,
  LinearProgress,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Checkbox,
  FormControlLabel,
  Link as MuiLink
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Info,
  Language
} from '@mui/icons-material';
import { registerViaAnef, clearError } from '../../redux/slices/clientAuthSlice';
import { RootState, AppDispatch } from '../../redux/store';
import { clientAuthApi } from '../../services/clientApi';

const steps = ['Identifiants ANEF', 'Création du compte', 'Confirmation'];

const languages = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
];

export default function ClientRegister() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error, emailVerificationSent } = useSelector(
    (state: RootState) => state.clientAuth
  );

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    anef_identifiant: '',
    anef_password: '',
    account_password: '',
    confirm_password: '',
    langue: 'fr',
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [showAnefPassword, setShowAnefPassword] = useState(false);
  const [showAccountPassword, setShowAccountPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isVerifyingAnef, setIsVerifyingAnef] = useState(false);
  const [anefVerificationError, setAnefVerificationError] = useState<string | null>(null);
  const [anefUserData, setAnefUserData] = useState<any>(null);

  // Validation
  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};

    if (step === 0) {
      // Étape 1 : Identifiants ANEF
      if (!formData.anef_identifiant) {
        errors.anef_identifiant = 'L\'identifiant ANEF est requis';
      } else if (formData.anef_identifiant.length < 6) {
        errors.anef_identifiant = 'L\'identifiant doit contenir au moins 6 caractères';
      }

      if (!formData.anef_password) {
        errors.anef_password = 'Le mot de passe ANEF est requis';
      }
    }

    if (step === 1) {
      // Étape 2 : Création du compte
      if (!formData.account_password) {
        errors.account_password = 'Le mot de passe est requis';
      } else if (formData.account_password.length < 8) {
        errors.account_password = 'Le mot de passe doit contenir au moins 8 caractères';
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.account_password)) {
        errors.account_password = 'Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre';
      }

      if (formData.account_password !== formData.confirm_password) {
        errors.confirm_password = 'Les mots de passe ne correspondent pas';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    // Si on est à l'étape 0 (identifiants ANEF), vérifier d'abord les credentials
    if (activeStep === 0) {
      setIsVerifyingAnef(true);
      setAnefVerificationError(null);

      try {
        const response = await clientAuthApi.verifyAnefCredentials(
          formData.anef_identifiant,
          formData.anef_password
        );

        if (response.data.success) {
          // Identifiants valides, stocker les données utilisateur
          setAnefUserData(response.data.data);
          setActiveStep((prev) => prev + 1);
        } else {
          // Identifiants invalides ou compte existe déjà
          setAnefVerificationError(response.data.message);
        }
      } catch (err: any) {
        console.error('Erreur vérification ANEF:', err);
        setAnefVerificationError(
          err.response?.data?.message ||
          'Impossible de vérifier les identifiants ANEF. Veuillez réessayer.'
        );
      } finally {
        setIsVerifyingAnef(false);
      }
    } else {
      // Autres étapes : passer directement à la suivante
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    dispatch(clearError());
    setAnefVerificationError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep(1)) return;

    if (!acceptedTerms) {
      setValidationErrors({
        ...validationErrors,
        terms: 'Vous devez accepter les Conditions Générales d\'Utilisation'
      });
      return;
    }

    const result = await dispatch(registerViaAnef({
      anef_identifiant: formData.anef_identifiant,
      anef_password: formData.anef_password,
      account_password: formData.account_password,
      langue: formData.langue,
    }));

    if (registerViaAnef.fulfilled.match(result)) {
      // Rediriger directement vers le dashboard (l'utilisateur est déjà connecté)
      navigate('/client/dashboard');
    }
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    // Effacer l'erreur de validation pour ce champ
    if (validationErrors[field]) {
      setValidationErrors({ ...validationErrors, [field]: '' });
    }
    // Effacer l'erreur de vérification ANEF si on modifie les identifiants
    if ((field === 'anef_identifiant' || field === 'anef_password') && anefVerificationError) {
      setAnefVerificationError(null);
    }
  };

  // Rendu des différentes étapes
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 3 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Utilisez vos identifiants ANEF pour créer votre compte. Vos informations
                personnelles seront automatiquement importées.
              </Typography>
            </Alert>

            {/* Afficher l'erreur de vérification ANEF */}
            {anefVerificationError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAnefVerificationError(null)}>
                {anefVerificationError}
              </Alert>
            )}

            {/* Afficher la barre de chargement lors de la vérification */}
            {isVerifyingAnef && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Vérification de vos identifiants ANEF en cours...
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              label="Identifiant ANEF"
              value={formData.anef_identifiant}
              onChange={handleChange('anef_identifiant')}
              error={!!validationErrors.anef_identifiant}
              helperText={validationErrors.anef_identifiant || 'Votre numéro d\'identification ANEF'}
              margin="normal"
              autoFocus
              disabled={isVerifyingAnef}
            />

            <TextField
              fullWidth
              label="Mot de passe ANEF"
              type={showAnefPassword ? 'text' : 'password'}
              value={formData.anef_password}
              onChange={handleChange('anef_password')}
              error={!!validationErrors.anef_password}
              helperText={validationErrors.anef_password || 'Le mot de passe de votre compte ANEF'}
              margin="normal"
              disabled={isVerifyingAnef}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowAnefPassword(!showAnefPassword)}
                      edge="end"
                      disabled={isVerifyingAnef}
                    >
                      {showAnefPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="caption">
                <strong>Sécurité :</strong> Votre mot de passe ANEF sera crypté (AES-256) et
                utilisé uniquement pour la synchronisation automatique de vos dossiers.
              </Typography>
            </Alert>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Créez votre mot de passe pour accéder à votre espace client.
              </Typography>
            </Alert>

            {/* Afficher les informations vérifiées depuis ANEF */}
            {anefUserData && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Informations importées depuis ANEF :</strong>
                </Typography>
                <Typography variant="caption" component="div">
                  • Nom : {anefUserData.nom || 'N/A'}
                </Typography>
                <Typography variant="caption" component="div">
                  • Prénom : {anefUserData.prenom || 'N/A'}
                </Typography>
                <Typography variant="caption" component="div">
                  • Email : {anefUserData.email || 'N/A'}
                </Typography>
                {anefUserData.telephone && (
                  <Typography variant="caption" component="div">
                    • Téléphone : {anefUserData.telephone}
                  </Typography>
                )}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Mot de passe du compte"
              type={showAccountPassword ? 'text' : 'password'}
              value={formData.account_password}
              onChange={handleChange('account_password')}
              error={!!validationErrors.account_password}
              helperText={validationErrors.account_password || 'Minimum 8 caractères, 1 majuscule, 1 minuscule, 1 chiffre'}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowAccountPassword(!showAccountPassword)}
                      edge="end"
                    >
                      {showAccountPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirmer le mot de passe"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirm_password}
              onChange={handleChange('confirm_password')}
              error={!!validationErrors.confirm_password}
              helperText={validationErrors.confirm_password}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Langue préférée</InputLabel>
              <Select
                value={formData.langue}
                label="Langue préférée"
                onChange={(e) => setFormData({ ...formData, langue: e.target.value })}
                startAdornment={
                  <InputAdornment position="start">
                    <Language />
                  </InputAdornment>
                }
              >
                {languages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(e) => {
                      setAcceptedTerms(e.target.checked);
                      if (e.target.checked && validationErrors.terms) {
                        setValidationErrors({ ...validationErrors, terms: '' });
                      }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    J'accepte les{' '}
                    <MuiLink
                      href="/cgu"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontWeight: 600, textDecoration: 'underline' }}
                    >
                      Conditions Générales d'Utilisation
                    </MuiLink>
                  </Typography>
                }
              />
              {validationErrors.terms && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                  {validationErrors.terms}
                </Typography>
              )}
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Inscription réussie !
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Un email de vérification a été envoyé à votre adresse email.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Vos avantages :</Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="Synchronisation automatique"
                  secondary="Vos dossiers ANEF mis à jour 3 fois par jour"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="Notifications temps réel"
                  secondary="Alertes push pour chaque mise à jour de dossier"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="Synthèse vocale"
                  secondary="Écoutez l'état de vos dossiers en plusieurs langues"
                />
              </ListItem>
            </List>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/client/login')}
              sx={{ mt: 3 }}
            >
              Se connecter maintenant
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        {/* Header with Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/logo.svg"
            alt="Logo"
            sx={{
              height: 60,
              mb: 3,
              filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.1))'
            }}
          />
          <Typography variant="h4" gutterBottom>
            Créez votre compte
          </Typography>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Loading */}
        {isLoading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Erreur */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
            {error}
          </Alert>
        )}

        {/* Contenu de l'étape */}
        {renderStepContent(activeStep)}

        {/* Boutons de navigation */}
        {activeStep < 2 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0 || isLoading}
              onClick={handleBack}
            >
              Retour
            </Button>
            <Box>
              {activeStep === 0 && (
                <Button
                  variant="text"
                  onClick={() => navigate('/client/login')}
                  sx={{ mr: 1 }}
                >
                  Déjà un compte ?
                </Button>
              )}
              <Button
                variant="contained"
                onClick={activeStep === steps.length - 2 ? handleSubmit : handleNext}
                disabled={isLoading || isVerifyingAnef}
              >
                {activeStep === 0 && isVerifyingAnef
                  ? 'Vérification...'
                  : activeStep === steps.length - 2
                  ? 'Créer mon compte'
                  : 'Suivant'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Informations supplémentaires */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Alert severity="info" icon={<Info />}>
          <Typography variant="caption">
            En créant un compte, vous acceptez nos conditions d'utilisation et notre politique de confidentialité.
            Vos données sont sécurisées et ne seront jamais partagées avec des tiers.
          </Typography>
        </Alert>
      </Box>
    </Container>
  );
}
